import React, { useState } from 'react';
import { auth } from './firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  GoogleAuthProvider, 
  signInWithPopup, 
  sendPasswordResetEmail 
} from 'firebase/auth';
import * as Icons from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc'; // Icon for Google

function Login() {
  const [view, setView] = useState("login"); // 'login', 'signup', 'reset'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  // UI States
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState(""); // For password reset confirmation
  const [isLoading, setIsLoading] = useState(false);

  // 1. Handle Email/Password Login & Signup
  const handleAuth = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");
    setIsLoading(true);

    try {
      if (view === "login") {
        await signInWithEmailAndPassword(auth, email, password);
      } else if (view === "signup") {
        await createUserWithEmailAndPassword(auth, email, password);
      } else if (view === "reset") {
        await sendPasswordResetEmail(auth, email);
        setSuccessMsg("Password reset link sent! Check your email.");
        setIsLoading(false);
        return; // Don't proceed to login logic
      }
    } catch (err) {
      let msg = err.message.replace("Firebase:", "").trim();
      if (msg.includes("invalid-credential")) msg = "Invalid email or password.";
      if (msg.includes("email-already-in-use")) msg = "Email already registered. Please log in.";
      if (msg.includes("weak-password")) msg = "Password should be at least 6 characters.";
      if (msg.includes("user-not-found")) msg = "No account found with this email.";
      setError(msg);
    } finally {
      if (view !== "reset") setIsLoading(false);
    }
  };

  // 2. Handle Google Login
  const handleGoogleLogin = async () => {
    setError("");
    setIsLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err) {
      setError("Google Sign-In failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // UI Helpers
  const toggleView = (newView) => {
    setView(newView);
    setError("");
    setSuccessMsg("");
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      width: '100vw', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      background: '#f8fafc', 
      fontFamily: '"Inter", sans-serif',
      padding: '20px', // Mobile padding
      boxSizing: 'border-box'
    }}>
      
      <div style={{ 
        width: '100%', 
        maxWidth: '420px', 
        background: 'white', 
        padding: '30px', 
        borderRadius: '24px', 
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.01)', 
        border: '1px solid #e2e8f0' 
      }}>
        
        {/* Logo & Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #10b981, #059669)', 
            width: '50px', 
            height: '50px', 
            borderRadius: '14px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            margin: '0 auto 15px', 
            color: 'white', 
            fontSize: '24px', 
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)' 
          }}>
            <Icons.FaWhatsapp />
          </div>
          <h2 style={{ margin: '0 0 6px 0', color: '#0f172a', fontSize: '22px', fontWeight: '800' }}>
            {view === 'login' && 'Welcome Back'}
            {view === 'signup' && 'Create Account'}
            {view === 'reset' && 'Reset Password'}
          </h2>
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
            {view === 'login' && 'Enter your credentials to access CRM.'}
            {view === 'signup' && 'Register your spa to get started.'}
            {view === 'reset' && 'Enter your email to receive a reset link.'}
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #fca5a5' }}>
            <Icons.FaExclamationCircle /> {error}
          </div>
        )}
        {successMsg && (
          <div style={{ background: '#dcfce7', color: '#15803d', padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #86efac' }}>
            <Icons.FaCheckCircle /> {successMsg}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleAuth} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#475569', fontSize: '13px', fontWeight: '600' }}>Email Address</label>
            <div style={{ position: 'relative' }}>
              <Icons.FaEnvelope style={{ position: 'absolute', top: '13px', left: '14px', color: '#94a3b8' }} />
              <input 
                type="email" 
                placeholder="hello@livaspa.com" 
                required 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                style={{ width: '100%', padding: '10px 15px 10px 38px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} 
              />
            </div>
          </div>

          {view !== 'reset' && (
            <div>
              <label style={{ display: 'block', marginBottom: '6px', color: '#475569', fontSize: '13px', fontWeight: '600', display: 'flex', justifyContent: 'space-between' }}>
                Password
                {view === 'login' && (
                  <span 
                    onClick={() => toggleView('reset')} 
                    style={{ color: '#10b981', cursor: 'pointer', fontWeight: '500', fontSize: '12px' }}>
                    Forgot?
                  </span>
                )}
              </label>
              <div style={{ position: 'relative' }}>
                <Icons.FaLock style={{ position: 'absolute', top: '13px', left: '14px', color: '#94a3b8' }} />
                <input 
                  type="password" 
                  placeholder="••••••••" 
                  required 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  style={{ width: '100%', padding: '10px 15px 10px 38px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} 
                />
              </div>
            </div>
          )}

          <button 
            type="submit" 
            disabled={isLoading} 
            style={{ 
              background: view === 'reset' ? '#334155' : 'linear-gradient(135deg, #10b981, #059669)', 
              color: 'white', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: '600', cursor: isLoading ? 'wait' : 'pointer', marginTop: '4px', fontSize: '14px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' 
            }}>
            {isLoading ? 'Processing...' : (
              view === 'login' ? 'Log In' : 
              view === 'signup' ? 'Create Account' : 
              'Send Reset Link'
            )}
          </button>
        </form>

        {/* Divider for Google Auth */}
        {view !== 'reset' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', margin: '20px 0', color: '#94a3b8', fontSize: '12px' }}>
              <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }}></div>
              <span style={{ padding: '0 10px' }}>OR</span>
              <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }}></div>
            </div>

            <button 
              type="button" 
              onClick={handleGoogleLogin} 
              disabled={isLoading}
              style={{ 
                width: '100%', background: 'white', color: '#334155', border: '1px solid #e2e8f0', padding: '12px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', transition: 'background 0.2s' 
              }}>
              <FcGoogle style={{ fontSize: '20px' }} /> 
              Continue with Google
            </button>
          </>
        )}

        {/* Footer Toggle */}
        <div style={{ textAlign: 'center', marginTop: '25px', fontSize: '13px', color: '#64748b' }}>
          {view === 'login' ? (
            <>Don't have an account? <span onClick={() => toggleView('signup')} style={{ color: '#10b981', fontWeight: '700', cursor: 'pointer' }}>Sign Up</span></>
          ) : view === 'signup' ? (
            <>Already have an account? <span onClick={() => toggleView('login')} style={{ color: '#10b981', fontWeight: '700', cursor: 'pointer' }}>Log In</span></>
          ) : (
            <span onClick={() => toggleView('login')} style={{ color: '#64748b', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              <Icons.FaArrowLeft /> Back to Login
            </span>
          )}
        </div>

      </div>
    </div>
  );
}

export default Login;